
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import TaskList from "@/components/TaskList";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import GpsTracker from "@/components/GpsTracker";
import WorkLocationOverview from "@/components/WorkLocationOverview";
import { NetworkStatus } from "@/components/network/NetworkStatus";
import { useIsMobile } from "@/hooks/use-mobile";
import { TimeOverview } from "@/components/time-tracking/TimeOverview";
import {
  CalendarDays,
  Clock,
  User,
  Building,
  ClipboardList,
  Timer,
  Menu,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { NotificationList } from "@/components/notifications/NotificationList";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<any>(null);
  const [activeTimeRegistration, setActiveTimeRegistration] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    getProfile();
    checkActiveTimeRegistration();
  }, []);

  const getProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profileData, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();

      if (error) throw error;
      setProfile(profileData);
    } catch (error) {
      toast({
        title: "Fejl ved hentning af profil",
        description: "Der opstod en fejl ved hentning af din profil",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const checkActiveTimeRegistration = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from("time_registrations")
        .select("*")
        .eq("user_id", session.user.id)
        .eq("status", "active")
        .maybeSingle();

      if (error) throw error;
      setActiveTimeRegistration(data);
    } catch (error) {
      console.error("Fejl ved tjek af aktiv tidsregistrering:", error);
    }
  };

  const DashboardContent = () => (
    <>
      <NetworkStatus />
      
      <div className="flex justify-between items-center mb-6 bg-white p-4 rounded-lg shadow">
        <h1 className="text-2xl font-bold text-gray-900">
          Velkommen, {profile?.first_name || 'Medarbejder'}
        </h1>
        {isMobile ? (
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Åbn menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/profile")}
                  className="w-full justify-start"
                >
                  <User className="h-4 w-4 mr-2" />
                  Min Profil
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/profile")}>
              <User className="h-4 w-4 mr-2" />
              Min Profil
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <NotificationList />
      </div>

      <Card className="p-4 md:p-6 mb-6 bg-gradient-to-br from-blue-50 to-indigo-50">
        <TimeOverview />
      </Card>

      <Card className="p-4 md:p-6 mb-6 bg-gradient-to-br from-purple-50 to-pink-50">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <Building className="h-5 w-5 mr-2 text-purple-600" />
          Arbejdslokationer
        </h2>
        <WorkLocationOverview />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-4 md:p-6 bg-gradient-to-br from-green-50 to-emerald-50">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Timer className="h-5 w-5 mr-2 text-green-600" />
            Tidsregistrering
          </h2>
          <GpsTracker />
        </Card>

        <Card className="p-4 md:p-6 bg-gradient-to-br from-yellow-50 to-amber-50">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <ClipboardList className="h-5 w-5 mr-2 text-amber-600" />
            Status
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center space-x-4 p-4 bg-white rounded-lg shadow-sm">
              <Clock className="h-8 w-8 text-blue-500" />
              <div>
                <h3 className="font-medium">Aktiv tid</h3>
                <p className="text-sm text-gray-500">
                  {activeTimeRegistration ? 'Igangværende' : 'Ingen aktiv registrering'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-white rounded-lg shadow-sm">
              <CalendarDays className="h-8 w-8 text-green-500" />
              <div>
                <h3 className="font-medium">Dagens opgaver</h3>
                <p className="text-sm text-gray-500">Se nedenfor</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-white rounded-lg shadow-sm col-span-full">
              <Building className="h-8 w-8 text-purple-500" />
              <div>
                <h3 className="font-medium">{profile?.company || 'Virksomhed'}</h3>
                <p className="text-sm text-gray-500">{profile?.email}</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Mine Opgaver</h2>
        </div>
        <TaskList />
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
        <DashboardContent />
      </div>
    </div>
  );
};

export default EmployeeDashboard;
